/**
 * Attribution Page
 * Patient-level and order-level attribution with multi-source support
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Target,
  Users,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { api } from '../../services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import toast from 'react-hot-toast';

interface PatientData {
  userId: string;
  email: string;
  firstTouchChannel: string;
  firstTouchSource: string;
  firstTouchCampaign: string;
  firstTouchDate: string;
  totalOrders: number;
  dispatchedOrders: number;
  totalRevenue: number;
  totalCOGS: number;
  totalAcquisitionCost: number;
  netProfit: number;
  location: any;
  deviceInfo: any;
  orders: any[];
}

interface OrderData {
  orderId: string;
  userId: string;
  email: string;
  placedAt: string;
  status: string;
  paymentStatus: string;
  total: number;
  totalCOGS: number;
  grossProfit: number;
  netProfit: number;
  acquisitionCost: number;
  attribution: {
    firstTouchChannel: string;
    firstTouchSource: string;
    lastTouchChannel: string;
    lastTouchSource: string;
    touchPoints: any[];
    markovWeights: Record<string, number>;
  };
}

const AttributionPage = () => {
  // Get selected company and data source from store
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // Filter to only use data sources that support 'attribution' module
  const attributionDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('attribution' as any) || 
    (ds.availableModules || []).includes('analytics')
  );
  const selectedDataSource = attributionDataSources[0] || null;

  const { companyId } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;
  const currency = selectedCompany?.settings?.currency || 'GBP';

  // Date range state (default: Last 30 days) - Load from localStorage
  const getDefaultDateRange = (): DateRange => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('attribution-dateRange');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved date range', e);
      }
    }
    
    // Default fallback
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 30 days',
    };
  };

  // Load filters from localStorage
  const getStoredValue = (key: string, defaultValue: string) => {
    const saved = localStorage.getItem(`attribution-${key}`);
    return saved || defaultValue;
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [view, setView] = useState<'patient' | 'order' | 'channel'>(
    (getStoredValue('view', 'patient') as 'patient' | 'order' | 'channel')
  );
  const [channelFilter, setChannelFilter] = useState<string>(getStoredValue('channelFilter', 'all'));
  const [statusFilter, setStatusFilter] = useState<string>(getStoredValue('statusFilter', 'all'));
  const [expandedPatient, setExpandedPatient] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Save to localStorage whenever filters change
  useEffect(() => {
    localStorage.setItem('attribution-dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('attribution-view', view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem('attribution-channelFilter', channelFilter);
  }, [channelFilter]);

  useEffect(() => {
    localStorage.setItem('attribution-statusFilter', statusFilter);
  }, [statusFilter]);

  // Fetch patient attribution
  const {
    data: patientResponse,
    isLoading: patientLoading,
    refetch: refetchPatient,
  } = useQuery({
    queryKey: [
      'patient-attribution',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      page,
      limit,
    ],
    queryFn: async () => {
      if (!companyId) throw new Error('Please select a company');

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit,
      };

      const response = await api.get('/api/v1/analytics/attribution/patient', { params });
      return response.data;
    },
    enabled: !!companyId && view === 'patient',
  });

  // Fetch order attribution
  const {
    data: orderResponse,
    isLoading: orderLoading,
    refetch: refetchOrder,
  } = useQuery({
    queryKey: [
      'order-attribution',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      channelFilter,
      statusFilter,
      page,
      limit,
    ],
    queryFn: async () => {
      if (!companyId) throw new Error('Please select a company');

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        channel: channelFilter,
        status: statusFilter,
        page,
        limit,
      };

      const response = await api.get('/api/v1/analytics/attribution/order', { params });
      return response.data;
    },
    enabled: !!companyId && view === 'order',
  });

  // Fetch channel performance
  const {
    data: channelResponse,
    isLoading: channelLoading,
    refetch: refetchChannel,
  } = useQuery({
    queryKey: [
      'channel-performance',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: async () => {
      if (!companyId) throw new Error('Please select a company');

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      const response = await api.get('/api/v1/analytics/attribution/channel-performance', { params });
      return response.data;
    },
    enabled: !!companyId && view === 'channel',
  });

  const patients: PatientData[] = patientResponse?.data?.patients || [];
  const orders: OrderData[] = orderResponse?.data?.orders || [];
  const channels = channelResponse?.data?.channels || [];
  const totals = channelResponse?.data?.totals || {};

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const getChannelColor = (channel: string) => {
    const colors: Record<string, string> = {
      email: 'bg-blue-100 text-blue-800',
      meta: 'bg-purple-100 text-purple-800',
      google: 'bg-red-100 text-red-800',
      awin: 'bg-green-100 text-green-800',
      tiktok: 'bg-pink-100 text-pink-800',
      direct: 'bg-gray-100 text-gray-800',
    };
    return colors[channel.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const handleRefresh = async () => {
    if (view === 'patient') await refetchPatient();
    else if (view === 'order') await refetchOrder();
    else await refetchChannel();
    toast.success('Data refreshed!');
  };

  const handleExport = () => {
    let data: any[] = [];
    let headers: string[] = [];

    if (view === 'patient') {
      headers = ['User ID', 'Email', 'First Touch', 'Total Orders', 'Revenue', 'Net Profit'];
      data = patients.map(p => [
        p.userId,
        p.email,
        p.firstTouchChannel,
        p.totalOrders,
        p.totalRevenue,
        p.netProfit,
      ]);
    } else if (view === 'order') {
      headers = ['Order ID', 'Email', 'Date', 'Status', 'Total', 'Last Touch'];
      data = orders.map(o => [
        o.orderId,
        o.email,
        o.placedAt,
        o.status,
        o.total,
        o.attribution.lastTouchChannel,
      ]);
    } else {
      headers = ['Channel', 'Spend', 'Revenue', 'Orders', 'ROAS', 'CPA'];
      data = channels.map(c => [
        c.channel,
        c.spend,
        c.revenue,
        c.orders,
        c.roas,
        c.cpa,
      ]);
    }

    const csv = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attribution-${view}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported successfully!');
  };

  const isLoading = patientLoading || orderLoading || channelLoading;

  if (!companyId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Target size={64} className="text-indigo-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Attribution & ROAS
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please select a company to view attribution data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Target className="text-indigo-500" />
              Acquisition Attribution & ROAS
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track patient-level attribution, order origins, and channel profitability
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
            >
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </div>

        {/* Data Source Info */}
        {selectedDataSource && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Data Source:</strong> {selectedDataSource.name} ({selectedDataSource.type})
              {selectedDataSource.purpose && ` - ${selectedDataSource.purpose}`}
            </p>
          </div>
        )}

        {/* View Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex overflow-x-auto">
              <button
                onClick={() => { setView('patient'); setPage(1); }}
                className={`px-6 py-3 font-medium transition flex items-center gap-2 whitespace-nowrap ${
                  view === 'patient'
                    ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Users size={18} />
                Patient Level
              </button>
              <button
                onClick={() => { setView('order'); setPage(1); }}
                className={`px-6 py-3 font-medium transition flex items-center gap-2 whitespace-nowrap ${
                  view === 'order'
                    ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <ShoppingCart size={18} />
                Order Level
              </button>
              <button
                onClick={() => setView('channel')}
                className={`px-6 py-3 font-medium transition flex items-center gap-2 whitespace-nowrap ${
                  view === 'channel'
                    ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <TrendingUp size={18} />
                Channel Performance
              </button>
            </div>
          </div>

          {/* Filters */}
          {view === 'order' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Channel
                  </label>
                  <select
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="all">All Channels</option>
                    <option value="email">Email</option>
                    <option value="meta">Meta (FB/IG)</option>
                    <option value="google">Google Ads</option>
                    <option value="awin">Awin (Affiliates)</option>
                    <option value="tiktok">TikTok</option>
                    <option value="direct">Direct</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="dispatched">Dispatched</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="animate-spin text-indigo-500" size={48} />
              </div>
            ) : (
              <>
                {/* Patient Level View */}
                {view === 'patient' && (
                  <div className="space-y-4">
                    {patients.length === 0 ? (
                      <div className="text-center py-12">
                        <Users size={48} className="text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">No patient data found</p>
                      </div>
                    ) : (
                      patients.map((patient) => (
                        <div
                          key={patient.userId}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                        >
                          <div
                            className="p-4 bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                            onClick={() =>
                              setExpandedPatient(
                                expandedPatient === patient.userId ? null : patient.userId
                              )
                            }
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {patient.email || patient.userId}
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelColor(
                                      patient.firstTouchChannel
                                    )}`}
                                  >
                                    {patient.firstTouchChannel}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Orders:</span>{' '}
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {patient.totalOrders}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Dispatched:</span>{' '}
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {patient.dispatchedOrders}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Revenue:</span>{' '}
                                    <span className="font-medium text-green-600 dark:text-green-400">
                                      {formatCurrency(patient.totalRevenue)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Acq. Cost:</span>{' '}
                                    <span className="font-medium text-orange-600 dark:text-orange-400">
                                      {formatCurrency(patient.totalAcquisitionCost)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Net Profit:</span>{' '}
                                    <span
                                      className={`font-medium ${
                                        patient.netProfit > 0
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-red-600 dark:text-red-400'
                                      }`}
                                    >
                                      {formatCurrency(patient.netProfit)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {expandedPatient === patient.userId ? (
                                <ChevronUp className="text-gray-400" />
                              ) : (
                                <ChevronDown className="text-gray-400" />
                              )}
                            </div>
                          </div>

                          {expandedPatient === patient.userId && (
                            <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                                Order History
                              </h4>
                              <div className="space-y-2">
                                {patient.orders.map((order: any) => (
                                  <div
                                    key={order.orderId}
                                    className="flex justify-between items-center text-sm p-2 bg-gray-50 dark:bg-gray-900/50 rounded"
                                  >
                                    <div>
                                      <span className="font-medium">{order.orderId}</span>
                                      <span className="text-gray-600 dark:text-gray-400 ml-2">
                                        {new Date(order.placedAt).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs ${getChannelColor(
                                          order.lastTouchChannel
                                        )}`}
                                      >
                                        {order.lastTouchChannel}
                                      </span>
                                      <span className="font-medium">{formatCurrency(order.total)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Order Level View */}
                {view === 'order' && (
                  <div className="overflow-x-auto">
                    {orders.length === 0 ? (
                      <div className="text-center py-12">
                        <ShoppingCart size={48} className="text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">No orders found</p>
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Order ID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Email
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              First Touch
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Last Touch
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Total
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              Net Profit
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {orders.map((order) => (
                            <tr key={order.orderId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {order.orderId}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {order.email}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {new Date(order.placedAt).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelColor(
                                    order.attribution.firstTouchChannel
                                  )}`}
                                >
                                  {order.attribution.firstTouchChannel}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelColor(
                                    order.attribution.lastTouchChannel
                                  )}`}
                                >
                                  {order.attribution.lastTouchChannel}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {order.status}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-right">
                                {formatCurrency(order.total)}
                              </td>
                              <td
                                className={`px-4 py-3 text-sm font-medium text-right ${
                                  order.netProfit > 0
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-600 dark:text-red-400'
                                }`}
                              >
                                {formatCurrency(order.netProfit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Channel Performance View */}
                {view === 'channel' && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
                        <div className="text-sm text-blue-600 dark:text-blue-400 mb-1">Total Spend</div>
                        <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                          {formatCurrency(totals.spend || 0)}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4">
                        <div className="text-sm text-green-600 dark:text-green-400 mb-1">Total Revenue</div>
                        <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                          {formatCurrency(totals.revenue || 0)}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4">
                        <div className="text-sm text-purple-600 dark:text-purple-400 mb-1">Total Orders</div>
                        <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                          {Math.round(totals.orders || 0)}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4">
                        <div className="text-sm text-orange-600 dark:text-orange-400 mb-1">Avg ROAS</div>
                        <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                          {totals.roas?.toFixed(2) || '0.00'}x
                        </div>
                      </div>
                    </div>

                    {/* Channel Table */}
                    <div className="overflow-x-auto">
                      {channels.length === 0 ? (
                        <div className="text-center py-12">
                          <TrendingUp size={48} className="text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-gray-400">No channel data found</p>
                        </div>
                      ) : (
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Channel
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Spend
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Revenue
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Orders
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Dispatched
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                ROAS
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                CPA
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                AOV
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                                Conv. Rate
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {channels.map((channel: any) => (
                              <tr key={channel.channel} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-4 py-3">
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${getChannelColor(
                                      channel.channel
                                    )}`}
                                  >
                                    {channel.channel}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-right">
                                  {formatCurrency(channel.spend)}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-green-600 dark:text-green-400 text-right">
                                  {formatCurrency(channel.revenue)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                                  {Math.round(channel.orders)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                                  {Math.round(channel.dispatchedOrders)}
                                </td>
                                <td
                                  className={`px-4 py-3 text-sm font-bold text-right ${
                                    channel.roas > 3
                                      ? 'text-green-600 dark:text-green-400'
                                      : channel.roas > 1
                                      ? 'text-yellow-600 dark:text-yellow-400'
                                      : 'text-red-600 dark:text-red-400'
                                  }`}
                                >
                                  {channel.roas.toFixed(2)}x
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                                  {formatCurrency(channel.cpa)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                                  {formatCurrency(channel.avgOrderValue)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                                  {channel.conversionRate.toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {(view === 'patient' || view === 'order') && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of{' '}
                {Math.ceil(
                  ((view === 'patient' ? patientResponse?.data?.pagination?.total : orderResponse?.data?.pagination?.total) || 0) /
                    limit
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={
                    page >=
                    Math.ceil(
                      ((view === 'patient' ? patientResponse?.data?.pagination?.total : orderResponse?.data?.pagination?.total) || 0) /
                        limit
                    )
                  }
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttributionPage;
